import React, { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  Button,
  Card,
  Grid,
  Icon,
  Message,
  Popup,
  Segment,
} from "semantic-ui-react";
import styled from "styled-components";

import { Input } from "simplydo/react/component-library";
import { useThrottle } from "simplydo/react/hooks";

import api from "api";
import util from "utils/utils";

import SuggestedIdeaCard from "components/lib/Cards/SuggestedIdeaCard";
import { ImageChooser } from "components/lib/Choosers";
import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";

const BodyContainer = styled(Grid.Column)`
  border: none;
`;

const SuggestedHeader = styled.div`
  > h4,
  span {
    margin: 0;
    color: #363636;
  }
  margin-top: 20px;
`;

const StyledParagraph = styled.p`
  margin-top: 0px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #2a2828;
  padding: 3px;
  padding-left: 6px;
  border-left: 4px solid #ffe4b5;
  background-color: #fffad1;
  display: block;
`;

const RemoveCoverImage = styled.div`
  display: flex;
  flex-direction: row;

  position: absolute;
  top: 5px;
  right: 5px;
`;

const ChangeCoverImage = styled.div``;

const IdeaCoverImage = styled.div`
  position: relative;
  width: 100%;
  border-radius: 5px;
  margin-top: 10px;
  height: 250px;
  background-size: cover;
  background-image: url(${({ $imageUrl }) => $imageUrl});
  background-position: center;
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    box-shadow: inset 0 0 0 2000px rgba(104, 104, 104, 0.3);
  }
  ${ChangeCoverImage} {
    opacity: ${({ theme }) => (!theme.sizes.isMobile ? 0 : 1)};
  }
  &:hover {
    ${ChangeCoverImage} {
      opacity: 1;
    }
  }
`;

const NameInput = ({
  ideaName,
  updateName,
  isDefaultName,
  setIdeaNameFocused,
}) => {
  const [defName, setDefName] = useState(isDefaultName ? "" : ideaName);
  useEffect(() => {
    setDefName(isDefaultName ? "" : ideaName);
  }, [isDefaultName, ideaName]);
  const { t } = useTranslation();

  return (
    <Input
      size="large"
      placeholder={t("ideas.submit.naming.placeholder")}
      fluid
      value={defName}
      onChange={(e) => {
        setDefName(e.target.value);
        if (ideaName !== e.target.value) {
          updateName(e.target.value);
        }
      }}
      onFocus={() => setIdeaNameFocused(true)}
      onBlur={() => setIdeaNameFocused(false)}
    />
  );
};

const IdeaNameField = ({
  challenge,
  isEditing,
  ideaCoverImage,
  ideaId,
  ideaName: propIdeaName,
  updateIdea,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [previewId, setPreviewId] = useState("");
  const [ideaNameFocused, setIdeaNameFocused] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const { t } = useTranslation();
  const coverImageEnabled = !challenge?.preventIdeaCoverImages;
  const filteredResults = searchResults.filter((r) => r._id !== ideaId);

  const isDefaultCoverImage =
    ideaCoverImage === "https://cdn.simplydo.co.uk/images/idea.png";

  const [defaultIdeaName, setDefaultIdeaName] = useState("");
  const isDefaultName = propIdeaName === defaultIdeaName;
  const [ideaName, setIdeaName] = useState(isDefaultName ? "" : propIdeaName);

  useEffect(() => {
    setDefaultIdeaName(
      `${t("common:capitalise", { key: "generic.idea" })} for ${challenge?.name}`,
    );
  }, [challenge?.name, t]);

  // Ensures name is correct if changed externally e.g. on submission
  useEffect(() => {
    setIdeaName(isDefaultName ? defaultIdeaName : propIdeaName);
  }, [defaultIdeaName, isDefaultName, propIdeaName]);

  const updateName = useCallback(
    (value) => {
      if (value.length === 0) {
        value = defaultIdeaName;
      }
      updateIdea(value, ["name"]);
    },
    [updateIdea, defaultIdeaName],
  );

  const throttledSearch = useThrottle((ideaId, term) => {
    if (!term) return;
    api.search.omni(
      { query: term, include: ["ideas"] },
      ({ ideas }) => {
        setSearchResults(ideas.filter((r) => r._id !== ideaId));
      },
      () => {},
    );
  }, 1000);

  const searchIdeas = useCallback(() => {
    throttledSearch(ideaId, ideaName);
  }, [ideaId, ideaName, throttledSearch]);

  useEffect(() => {
    if (isEditing && !isDefaultName && ideaName?.length > 2) {
      searchIdeas(ideaName);
    } else {
      setSearchResults([]);
    }
  }, [isEditing, ideaName, isDefaultName, searchIdeas]);

  const setCoverImage = useCallback(
    (filename, _name, url) => {
      updateIdea(filename, ["coverImage"]);
      updateIdea(url, ["coverImageUrl"], true);
      if (!url) {
        toast.success("Cover image removed.");
        updateIdea(
          "https://cdn.simplydo.co.uk/images/idea.png",
          ["coverImageUrl"],
          true,
        );
      }
    },
    [updateIdea],
  );

  useEffect(() => {
    if (filteredResults.length && ideaNameFocused) {
      setPopupOpen(true);
    }
    if (ideaNameFocused && !filteredResults.length) {
      setPopupOpen(false);
    }
  }, [filteredResults, ideaNameFocused]);

  const ChangeCoverImageButton = useMemo(
    () => (
      <div style={isDefaultCoverImage ? { marginTop: 10 } : {}}>
        <ImageChooser
          forType="idea"
          forId={ideaId}
          onComplete={setCoverImage}
          trigger={
            <Button icon="file image" primary fluid={isDefaultCoverImage}>
              <Popup
                content={t("ideas.coverimage.info")}
                trigger={
                  <p>
                    {isDefaultCoverImage ?
                      t("users.edit.coverImage.select")
                    : t("users.edit.coverImage.change")}
                  </p>
                }
                position="top center"
                wide
              />
            </Button>
          }
        />
      </div>
    ),
    [isDefaultCoverImage, setCoverImage, ideaId, t],
  );

  if (!isEditing) return null;
  const sorted = filteredResults.sort(
    (a, b) => b?.completeness || 0 - a?.completeness || 0,
  );
  return (
    <BodyContainer style={{ borderRadius: 5, marginBottom: 25 }} computer={16}>
      <HandledIdeaPreview
        ideaId={previewId}
        onCloseModal={() => setPreviewId()}
      />
      <Segment style={{ borderRadius: 5, borderColor: "#3daaaea8" }}>
        <div style={{ marginBottom: 5 }}>
          <h3>{t("common:capitalise", { key: "generic.idea" })} name</h3>
        </div>
        <StyledParagraph>
          <Icon name="info circle" />
          {challenge?.ideaNameHelpText || t("ideas.name.auto.suggestion")}
        </StyledParagraph>
        <NameInput
          isDefaultName={isDefaultName}
          ideaName={ideaName}
          updateName={updateName}
          setIdeaNameFocused={setIdeaNameFocused}
        />
        {filteredResults.length > 0 && popupOpen && (
          <Message>
            <SuggestedHeader>
              <h4>Related {t("generic.ideas")}</h4>
              <span>
                Here are some similar {t("generic.ideas")} we found based on the
                name you entered...
              </span>
            </SuggestedHeader>
            <Card.Group
              style={{ marginBottom: 10, marginTop: 5 }}
              itemsPerRow={3}
              stackable
            >
              {sorted.slice(0, 3).map((result) => (
                <SuggestedIdeaCard
                  onHeaderClicked={() => setPreviewId(result._id)}
                  key={result._id}
                  idea={result}
                />
              ))}
            </Card.Group>
          </Message>
        )}
        {coverImageEnabled && (
          <>
            <h5>{t("users.edit.coverImage.title")}</h5>
            {isDefaultCoverImage ?
              ChangeCoverImageButton
            : <IdeaCoverImage
                $imageUrl={util.mixinCssUrlFallback(
                  ideaCoverImage,
                  util.ideaCoverImage(),
                )}
              >
                {!isDefaultCoverImage ?
                  <RemoveCoverImage>
                    {ChangeCoverImageButton}
                    <Button
                      icon="trash"
                      content="Remove cover image"
                      onClick={() =>
                        util
                          .confirm(
                            "Remove cover image",
                            `Are you sure you want to remove the cover image for this ${t("generic.idea")}?`,
                          )
                          .then(() => setCoverImage(null, null, null))
                          .catch(() => {})
                      }
                    />
                  </RemoveCoverImage>
                : null}
              </IdeaCoverImage>
            }
          </>
        )}
      </Segment>
    </BodyContainer>
  );
};

export default IdeaNameField;
